import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useCart } from '../store/cart';
import { NewsletterModal } from './NewsletterModal';
import { ConsentModal } from './ConsentModal';
import { openConsentSettings } from '../consent/consentMode';

/** Ustawia tytuł dokumentu. Wywoływane przez każdą podstronę. */
export function useTitle(title: string) {
  useEffect(() => {
    document.title = `${title} — Nordform`;
  }, [title]);
}

/** Jak długo czekamy po zmianie URL, zanim wyślemy wirtualną odsłonę. */
const VIRTUAL_PAGEVIEW_DEBOUNCE_MS = 500;

/**
 * Wirtualne odsłony dla nawigacji w SPA.
 *
 * Celowo NIE opieramy się na natywnym `gtm.historyChange`. Ten trigger w GTM
 * odpala się w momencie wywołania `history.pushState`/`replaceState` przez
 * router — a to następuje ZANIM React zdąży wyrenderować nową stronę i
 * ustawić jej tytuł przez `useTitle`. Zmierzone empirycznie: w tej
 * aplikacji `{{Page Title}}` w takim momencie to tytuł POPRZEDNIEJ strony.
 *
 * Zamiast tego sami wypychamy zdarzenie z efektu Reacta, który uruchamia się
 * PO commicie nowej strony. Działa to niezawodnie, bo efekty komponentów
 * potomnych (tu: `useTitle` w komponencie strony wewnątrz <Outlet>)
 * uruchamiają się PRZED efektami rodzica (tego hooka w <Layout>) w obrębie
 * tego samego cyklu renderowania — więc `document.title` jest już aktualny.
 *
 * Debounce chroni przed zalaniem GA4 zdarzeniami: wyszukiwarka w katalogu
 * zmienia URL przez `replaceState` przy KAŻDYM wciśniętym klawiszu. Liczy
 * się tylko URL, na którym użytkownik faktycznie się zatrzymał.
 *
 * Strażnik pierwszej odsłony NIE jest zwykłą flagą `useRef(true)` — taka
 * flaga nie przeżywa podwójnego wywołania efektów przez <StrictMode> przy
 * montowaniu (React celowo odpala mount → cleanup → mount, żeby wyłapać
 * efekty niebezpieczne przy powtórnym uruchomieniu; potwierdzone empirycznie:
 * bez tej poprawki pierwsze wejście na stronę dokładało dodatkowy, zbędny
 * `virtual_page_view` obok automatycznego page_view z tagu Google Tag).
 * Zamiast tego porównujemy bieżącą ścieżkę z ostatnią ścieżką, dla której
 * zdarzenie faktycznie wystrzeliło — referencja aktualizuje się TYLKO
 * wewnątrz odpalonego timera, więc oba przebiegi StrictMode widzą tę samą,
 * niezmienioną wartość i żaden z nich nie weźmie pierwszej odsłony za
 * nawigację. Późniejszy realny powrót na tę samą ścieżkę nadal poprawnie
 * się liczy, bo referencja do tego czasu już się zaktualizowała.
 */
function useVirtualPageview() {
  const location = useLocation();
  const path = location.pathname + location.search;
  const previousPath = useRef(path);

  useEffect(() => {
    if (path === previousPath.current) return;

    const timer = window.setTimeout(() => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'virtual_page_view',
        page_location: window.location.href,
        page_path: path,
        page_title: document.title,
        page_referrer: window.location.origin + previousPath.current,
      });
      previousPath.current = path;
    }, VIRTUAL_PAGEVIEW_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [path]);
}

export function Layout() {
  const { count, user } = useCart();
  const [menu, setMenu] = useState(false);
  const loc = useLocation();

  useVirtualPageview();

  useEffect(() => {
    setMenu(false);
    window.scrollTo(0, 0);
  }, [loc.pathname]);

  const link = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-1.5 rounded-lg text-sm transition ${
      isActive ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
    }`;

  return (
    <div className="flex min-h-full flex-col">
      <div className="bg-[#22d3a6]/10 px-4 py-1.5 text-center text-[12px] text-[#7ef0d0]">
        Darmowa dostawa od 2000 zł · Zwroty do 30 dni
      </div>

      <header className="sticky top-0 z-30 border-b border-[#26304d] bg-[#0b1020]/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-1 px-4 py-2.5">
          <Link to="/" className="mr-3 flex items-center gap-2 font-semibold text-white">
            <span className="text-lg">🪑</span>
            <span>Nordform</span>
          </Link>

          <nav className="flex items-center gap-0.5">
            <NavLink to="/" end className={link}>
              Start
            </NavLink>
            <NavLink to="/katalog" className={link}>
              Katalog
            </NavLink>
            <div className="relative">
              <button
                onClick={() => setMenu((m) => !m)}
                className="rounded-lg px-3 py-1.5 text-sm text-slate-400 hover:bg-white/5 hover:text-white"
              >
                Kategorie ▾
              </button>
              {menu && (
                <div className="absolute left-0 top-full z-40 mt-1 w-56 rounded-xl border border-[#26304d] bg-[#0f1526] p-1.5 shadow-2xl">
                  {['Meble', 'Oświetlenie', 'Elektronika', 'Akcesoria'].map((c) => (
                    <Link
                      key={c}
                      to={`/katalog?kategoria=${encodeURIComponent(c)}`}
                      className="block rounded-lg px-2.5 py-2 text-[13px] text-slate-200 hover:bg-white/5"
                    >
                      {c}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <NavLink to="/kontakt" className={link}>
              Kontakt
            </NavLink>
          </nav>

          <div className="ml-auto flex items-center gap-1">
            <NavLink to="/konto" className={link}>
              {user ? `👤 ${user.email.split('@')[0]}` : 'Zaloguj'}
            </NavLink>
            <NavLink
              to="/koszyk"
              className="relative rounded-lg border border-[#26304d] px-3 py-1.5 text-sm text-slate-200 hover:border-[#4f8cff]"
            >
              🛒
              {count > 0 && (
                <span className="absolute -right-1.5 -top-1.5 rounded-full bg-[#4f8cff] px-1.5 text-[10px] font-semibold text-white">
                  {count}
                </span>
              )}
            </NavLink>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">
        <Outlet />
      </main>

      <footer className="mt-10 border-t border-[#26304d] px-4 py-8">
        <div className="mx-auto grid max-w-6xl gap-6 text-[12.5px] text-slate-400 sm:grid-cols-3">
          <div>
            <p className="mb-2 font-semibold text-white">Nordform</p>
            <p className="leading-relaxed">
              Meble i akcesoria do pracy. Sklep demonstracyjny — zamówienia nie są realizowane.
            </p>
          </div>
          <div>
            <p className="mb-2 font-semibold text-white">Obsługa klienta</p>
            <ul className="space-y-1">
              <li>
                <Link to="/kontakt" className="hover:text-white">
                  Kontakt
                </Link>
              </li>
              <li>
                <Link to="/konto" className="hover:text-white">
                  Moje zamówienia
                </Link>
              </li>
              <li>
                <Link to="/kontakt#zwroty" className="hover:text-white">
                  Zwroty i reklamacje
                </Link>
              </li>
              <li>
                {/* Wycofanie zgody musi być tak samo łatwe jak jej udzielenie. */}
                <button onClick={openConsentSettings} className="hover:text-white">
                  Ustawienia prywatności
                </button>
              </li>
            </ul>
          </div>
          <div>
            <p className="mb-2 font-semibold text-white">Newsletter</p>
            <p className="mb-2 leading-relaxed">Nowości i kody rabatowe raz w miesiącu.</p>
            <a
              href="https://nordform.example.com"
              target="_blank"
              rel="noreferrer"
              className="affiliate-store-link text-[#9ec5ff] hover:underline"
            >
              Sklep partnerski ↗
            </a>
          </div>
        </div>
      </footer>

      <ConsentModal />
      <NewsletterModal />
    </div>
  );
}
